import { RefObject, useEffect, useRef, useState } from 'react'
import { Element, scroller } from 'react-scroll'
import { Box, CircularProgress, Modal, Typography } from '@material-ui/core'
import EditOutlined from '@material-ui/icons/EditOutlined'
import EmojiObjectsOutlined from '@material-ui/icons/EmojiObjectsOutlined'
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos'
import clsx from 'clsx'

import BackIcon from 'assets/Icons/BackIcon'
import PromptModal from 'components/GoalWizard/PromptModal'
import Tooltip from 'components/Tooltip'
import BulkRemunerationValuesTable, { StoreRemunerationValue } from '../BulkRemunerationValuesTable'
import DailyGoalsTable from '../DailyGoalsTable'
import { NumberField } from '../BulkRemunerationValuesTable/types'
import { BulkGoalClasses, DailyGoalMode, DailyGoalValues } from '../../types'
import { useValuesStepStyles } from './styles'

export type { DailyGoalMode } from '../../types'
type PendingSubstepEdit = 'mode' | 'manual' | null
const SUBSTEP_SCROLL_OFFSET = -170
const MANUAL_GOALS_TOOLTIP_ID = 'bulk-goal-manual-goals-incomplete'

interface ValuesStepProps {
  classes: BulkGoalClasses
  dailyGoalMode: DailyGoalMode
  dailyGoalValues: DailyGoalValues
  isSetupComplete: boolean
  selectedMonth: string
  selectedMonthLabel: string
  values: StoreRemunerationValue[]
  valuesAutofillStatus: 'idle' | 'loading' | 'applied' | 'skipped'
  onSetupCompleteChange: (isComplete: boolean) => void
  onDailyGoalModeChange: (mode: DailyGoalMode) => void
  onDailyGoalValuesChange: (values: DailyGoalValues) => void
  onManualGoalsComplete: (values: DailyGoalValues) => void
  onResetValues: () => void
  onApplyToAll: (field: NumberField, value: number) => void
  onChange: (storeId: string, field: NumberField, value: number | null) => void
}

const ValuesStep = ({
  classes,
  dailyGoalMode,
  dailyGoalValues,
  isSetupComplete,
  selectedMonth,
  selectedMonthLabel,
  values,
  valuesAutofillStatus,
  onSetupCompleteChange,
  onDailyGoalModeChange,
  onDailyGoalValuesChange,
  onManualGoalsComplete,
  onResetValues,
  onApplyToAll,
  onChange
}: ValuesStepProps) => {
  const localClasses = useValuesStepStyles()
  const [manualGoalsComplete, setManualGoalsComplete] = useState(false)
  const [isManualTableVisible, setIsManualTableVisible] = useState(
    dailyGoalMode === 'manual' && isSetupComplete
  )
  const [pendingSubstepEdit, setPendingSubstepEdit] = useState<PendingSubstepEdit>(null)
  const modeTitleRef = useRef<HTMLHeadingElement>(null)
  const manualGoalsTitleRef = useRef<HTMLHeadingElement>(null)
  const remunerationTitleRef = useRef<HTMLHeadingElement>(null)
  const shouldFocusManualGoalsRef = useRef(false)
  const shouldFocusRemunerationRef = useRef(false)
  const isModeSelectionComplete = isSetupComplete || (dailyGoalMode === 'manual' && isManualTableVisible)

  const scrollToSubstep = (name: string, titleRef: RefObject<HTMLHeadingElement>, offset = SUBSTEP_SCROLL_OFFSET) => {
    window.requestAnimationFrame(() => {
      titleRef.current?.focus({ preventScroll: true })
      scroller.scrollTo(name, { duration: 350, offset, smooth: true })
    })
  }

  const revealManualGoals = () => {
    shouldFocusManualGoalsRef.current = true
    setIsManualTableVisible(true)
  }

  const revealRemuneration = () => {
    shouldFocusRemunerationRef.current = true
    if (dailyGoalMode === 'manual') onManualGoalsComplete(dailyGoalValues)
    onSetupCompleteChange(true)
  }

  useEffect(() => {
    if (!isManualTableVisible || !shouldFocusManualGoalsRef.current) return undefined
    const frameId = window.requestAnimationFrame(() => {
      shouldFocusManualGoalsRef.current = false
      manualGoalsTitleRef.current?.focus({ preventScroll: true })
      scroller.scrollTo('manual-goals', { duration: 350, offset: SUBSTEP_SCROLL_OFFSET, smooth: true })
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [isManualTableVisible])

  useEffect(() => {
    if (!isSetupComplete || !shouldFocusRemunerationRef.current) return undefined
    const frameId = window.requestAnimationFrame(() => {
      shouldFocusRemunerationRef.current = false
      remunerationTitleRef.current?.focus({ preventScroll: true })
      scroller.scrollTo('remuneration', { duration: 350, offset: SUBSTEP_SCROLL_OFFSET, smooth: true })
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [isSetupComplete])

  const selectMode = (mode: Exclude<DailyGoalMode, null>) => {
    setIsManualTableVisible(false)
    setManualGoalsComplete(false)
    onDailyGoalModeChange(mode)
    onSetupCompleteChange(false)
  }

  const advanceModeSelection = () => {
    if (dailyGoalMode === 'manual') {
      revealManualGoals()
      return
    }
    revealRemuneration()
  }

  const confirmSubstepEdit = () => {
    onResetValues()
    onSetupCompleteChange(false)
    if (pendingSubstepEdit === 'mode') {
      onDailyGoalValuesChange({})
      setManualGoalsComplete(false)
      setIsManualTableVisible(false)
      scrollToSubstep('daily-goal-mode', modeTitleRef)
    } else {
      scrollToSubstep('manual-goals', manualGoalsTitleRef, SUBSTEP_SCROLL_OFFSET)
    }
    setPendingSubstepEdit(null)
  }

  return (
    <div className={classes.valuesContainer}>
      <Element name='daily-goal-mode' className={localClasses.setupSection}>
        <div
          aria-disabled={isModeSelectionComplete}
          className={clsx(localClasses.substepContent, isModeSelectionComplete && localClasses.completedSubstep)}
          data-values-substep='mode'
        >
          <h1 ref={modeTitleRef} className={classes.title} tabIndex={-1}>Como deseja definir as metas diárias?</h1>
          <Typography className={classes.subtitle}>
            Escolha como os valores de cada dia serão distribuídos entre as lojas em {selectedMonthLabel}.
          </Typography>

          <div className={localClasses.choiceGrid}>
            <button
              aria-pressed={dailyGoalMode === 'ai'}
              className={clsx(localClasses.choiceCard, dailyGoalMode === 'ai' && localClasses.selectedChoice)}
              disabled={isModeSelectionComplete}
              type='button'
              onClick={() => selectMode('ai')}
            >
              <span className={localClasses.choiceIcon}><EmojiObjectsOutlined /></span>
              <span className={localClasses.choiceContent}>
                <strong>Distribuir com a inteligência da Neopro</strong>
                <span>A Neopro sugere a distribuição diária com base na meta mensal e no histórico das lojas.</span>
                <span className={localClasses.recommended}>Recomendado</span>
              </span>
            </button>
            <button
              aria-pressed={dailyGoalMode === 'manual'}
              className={clsx(localClasses.choiceCard, dailyGoalMode === 'manual' && localClasses.selectedChoice)}
              disabled={isModeSelectionComplete}
              type='button'
              onClick={() => selectMode('manual')}
            >
              <span className={localClasses.choiceIcon}><EditOutlined /></span>
              <span className={localClasses.choiceContent}>
                <strong>Preencher metas diárias manualmente</strong>
                <span>Digite os valores ou copie uma linha ou bloco diretamente da sua planilha.</span>
              </span>
            </button>
          </div>
        </div>

        {isModeSelectionComplete ? (
          <div className={clsx(
            localClasses.advanceAction,
            localClasses.modeAdvanceAction
          )}>
            <button
              aria-label='Alterar forma de definição das metas diárias'
              className={localClasses.editButton}
              type='button'
              onClick={() => setPendingSubstepEdit('mode')}
            >
              Alterar
              <EditOutlined />
            </button>
          </div>
        ) : dailyGoalMode && (
          <div className={clsx(
            localClasses.advanceAction,
            localClasses.modeAdvanceAction,
            localClasses.primaryAdvanceAction
          )}>
            <button
              className={localClasses.advanceButton}
              type='button'
              onClick={advanceModeSelection}
            >
              Avançar
              <ArrowForwardIos />
            </button>
          </div>
        )}
      </Element>

      {dailyGoalMode === 'manual' && isManualTableVisible && (
        <section className={localClasses.manualSection}>
          <Element
            name='manual-goals'
            aria-disabled={isSetupComplete}
            className={clsx(localClasses.manualContent, isSetupComplete && localClasses.completedSubstep)}
            data-values-substep='manual'
          >
            <div className={localClasses.manualHeader}>
              <h2 ref={manualGoalsTitleRef} tabIndex={-1}>Metas diárias por loja</h2>
            </div>
            <DailyGoalsTable
              disabled={isSetupComplete}
              month={selectedMonth}
              stores={values}
              values={dailyGoalValues}
              onChange={onDailyGoalValuesChange}
              onCompletionChange={setManualGoalsComplete}
            />
          </Element>
          <div className={clsx(
            localClasses.advanceAction,
            !isSetupComplete && localClasses.primaryAdvanceAction
          )}>
            {isSetupComplete ? (
              <button
                aria-label='Alterar metas diárias'
                className={localClasses.editButton}
                type='button'
                onClick={() => setPendingSubstepEdit('manual')}
              >
                Alterar
                <EditOutlined />
              </button>
            ) : (
              <>
                {!manualGoalsComplete && (
                  <Tooltip
                    fixed
                    place='top'
                    id={MANUAL_GOALS_TOOLTIP_ID}
                    content={<p>Preencha todas as metas diárias corretamente para continuar.</p>}
                  />
                )}
                <span
                  className={localClasses.advanceTooltipTarget}
                  data-tip={!manualGoalsComplete ? true : undefined}
                  data-for={!manualGoalsComplete ? MANUAL_GOALS_TOOLTIP_ID : undefined}
                >
                  <button
                    className={localClasses.advanceButton}
                    disabled={!manualGoalsComplete}
                    type='button'
                    onClick={revealRemuneration}
                  >
                    Avançar
                    <ArrowForwardIos />
                  </button>
                </span>
              </>
            )}
          </div>
        </section>
      )}

      {isSetupComplete && (
        <section className={localClasses.remunerationSection}>
          {valuesAutofillStatus === 'loading' && (
            <Box mb={2} display='flex' alignItems='center' justifyContent='center'>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              <Typography color='textSecondary' variant='body2'>
                Analisando configurações anteriores...
              </Typography>
            </Box>
          )}
          <Element name='remuneration' className={classes.valueElement}>
            <h1 ref={remunerationTitleRef} className={classes.title} tabIndex={-1}>Valores e remuneração por loja</h1>
            <Typography className={classes.subtitle}>
              Revise piso, DSR e metas principais de cada loja para {selectedMonthLabel}.
            </Typography>

            <BulkRemunerationValuesTable
              isTotalReadOnly={dailyGoalMode === 'manual'}
              values={values}
              onChange={onChange}
              onApplyToAll={onApplyToAll}
            />
          </Element>
        </section>
      )}

      <Modal open={Boolean(pendingSubstepEdit)} onClose={() => setPendingSubstepEdit(null)}>
        <PromptModal
          description='Se você alterar essa informação, as etapas abaixo serão perdidas.'
          icon={<BackIcon />}
          isAlert
          rightLoading={false}
          rightTitle='Alterar'
          title='Deseja continuar a alteração?'
          onClose={() => setPendingSubstepEdit(null)}
          onLeft={() => setPendingSubstepEdit(null)}
          onRight={confirmSubstepEdit}
        >
          <ul className={localClasses.resetList}>
            {pendingSubstepEdit === 'mode' && dailyGoalMode === 'manual' && <li>Metas diárias por loja</li>}
            {pendingSubstepEdit && <li>Valores e remuneração por loja</li>}
          </ul>
        </PromptModal>
      </Modal>
    </div>
  )
}

export default ValuesStep
